import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { SpotCard } from '@/components/SpotCard';
import { Seo } from '@/components/Seo';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Loader2, MapPin } from 'lucide-react';

const markerIcon = L.divIcon({ className: '', html: '<div class="kite-marker"></div>', iconSize: [16, 16], iconAnchor: [8, 8] });

export default function Spots() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windNow, setWindNow] = useState({});
  const [q, setQ] = useState(() => {
    // Honour ?q= from the URL so the JSON-LD SearchAction (/spots?q=...) works.
    if (typeof window !== 'undefined') {
      return new URLSearchParams(window.location.search).get('q') || '';
    }
    return '';
  });
  const [type, setType] = useState('all');
  const [level, setLevel] = useState('all');
  const [month, setMonth] = useState('all');

  useEffect(() => { api.get('/spots').then(({ data }) => setSpots(data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { api.get('/spots/wind-now').then(({ data }) => setWindNow(data.wind || {})).catch(() => {}); }, []);

  const months = t('datetime.monthsShort', { returnObjects: true });
  const monthList = Array.isArray(months) ? months : [];

  const filtered = useMemo(() => spots.filter((s) => {
    if (type !== 'all' && s.type !== type) return false;
    if (level !== 'all' && s.level !== level && s.level !== 'all') return false;
    if (month !== 'all' && !(s.best_months || []).includes(Number(month))) return false;
    if (q) { const ql = q.toLowerCase(); if (!s.name.toLowerCase().includes(ql) && !s.country.toLowerCase().includes(ql) && !(s.region||'').toLowerCase().includes(ql)) return false; }
    return true;
  }), [spots, type, level, month, q]);

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <Seo
        title={t('spots.seoTitle')}
        description={t('spots.seoDesc')}
        canonical={typeof window !== 'undefined' ? `${window.location.origin}/spots` : undefined}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Kitesurf spots',
          numberOfItems: spots.length,
          itemListElement: spots.slice(0, 30).map((s, i) => ({
            '@type': 'ListItem', position: i + 1, name: s.name,
            url: typeof window !== 'undefined' ? `${window.location.origin}/spots/${s.id}` : undefined,
          })),
        }}
      />
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="label-eyebrow text-[#48CAE4] mb-2">{t('spots.eyebrow')}</div>
          <h1 className="font-display text-4xl sm:text-5xl">{t('spots.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('spots.count', { count: filtered.length })}</p>
        </div>
      </div>

      {/* filters */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="relative col-span-2 lg:col-span-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('spots.searchPlaceholder')} className="pl-9 bg-[#12161B] border-white/15" data-testid="spot-search" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="bg-[#12161B] border-white/15" data-testid="spot-filter-type"><SelectValue placeholder={t('spots.type')} /></SelectTrigger>
          <SelectContent className="bg-[#12161B] border-white/15 text-white">
            <SelectItem value="all">{t('spots.allTypes')}</SelectItem><SelectItem value="flat">{t('spots.flat')}</SelectItem><SelectItem value="wave">{t('spots.wave')}</SelectItem><SelectItem value="freestyle">{t('spots.freestyle')}</SelectItem><SelectItem value="chop">{t('spots.chop')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={level} onValueChange={setLevel}>
          <SelectTrigger className="bg-[#12161B] border-white/15" data-testid="spot-filter-level-select"><SelectValue placeholder={t('spots.level')} /></SelectTrigger>
          <SelectContent className="bg-[#12161B] border-white/15 text-white">
            <SelectItem value="all">{t('spots.allLevels')}</SelectItem><SelectItem value="beginner">{t('spots.beginner')}</SelectItem><SelectItem value="intermediate">{t('spots.intermediate')}</SelectItem><SelectItem value="advanced">{t('spots.advanced')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="bg-[#12161B] border-white/15" data-testid="spot-filter-month"><SelectValue placeholder={t('spots.bestMonth')} /></SelectTrigger>
          <SelectContent className="bg-[#12161B] border-white/15 text-white max-h-64">
            <SelectItem value="all">{t('spots.anyMonth')}</SelectItem>
            {monthList.map((m, i) => <SelectItem key={m} value={String(i+1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 rounded-2xl overflow-hidden border border-white/10" style={{ height: '72vh' }} data-testid="spots-map">
          <MapContainer center={[25, 5]} zoom={2} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
            <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution='&copy; OpenStreetMap &copy; CARTO' />
            {filtered.map((s) => (
              <Marker key={s.id} position={[s.gps_lat, s.gps_lng]} icon={markerIcon}>
                <Popup>
                  <div style={{ minWidth: 160 }}>
                    <div className="font-display text-xl">{s.name}</div>
                    <div className="text-xs" style={{ color: '#B9C4CF' }}>{s.country} · {s.type}</div>
                    <button onClick={() => navigate(`/spots/${s.id}`)} style={{ marginTop: 8, color: '#00B4D8', fontWeight: 600, fontSize: 13 }}>{t('spots.viewSpot')}</button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        <div className="lg:col-span-2">
          {loading ? <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-[#00B4D8] animate-spin" /></div> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-1 gap-4 lg:max-h-[72vh] lg:overflow-y-auto lg:pr-2">
              {filtered.map((s) => <SpotCard key={s.id} spot={s} nowKn={windNow[s.id]?.wind} user={user} />)}
              {filtered.length === 0 && <div className="text-center text-muted-foreground py-10"><MapPin className="w-6 h-6 mx-auto mb-2" />{t('spots.noMatch')}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
